import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../../LangContext';
import { getCartKey } from '../../pages/bazaar/cartUtils';

export default function ProductCard({ product, onAddToCart, viewMode = 'grid', showTrending = false }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [addedFeedback, setAddedFeedback] = useState(false);

  const { name, price, category, seller_name, seller_id, seller_location, stock_quantity, photos = [], description, is_package, package_items, order_count, discount_price } = product;

  const outOfStock = stock_quantity === 0;
  const lowStock = !outOfStock && Number(stock_quantity) <= 5;
  const photoUrl = photos[imgIndex]?.url || photos[0]?.url;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) return;
    const token = localStorage.getItem('buyerToken') || localStorage.getItem('userToken');
    if (!token) { setShowLoginPrompt(true); return; }
    onAddToCart && onAddToCart(product);
    setAddedFeedback(true);
    setTimeout(() => setAddedFeedback(false), 1500);
  };

  const isListMode = viewMode === 'list';

  return (
    <>
      {/* Login prompt modal */}
      {showLoginPrompt && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(30, 41, 59, 0.8)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }}
          onClick={() => setShowLoginPrompt(false)}
        >
          <div
            className="creative-card"
            style={{ padding: '48px 32px', maxWidth: 400, width: '100%', textAlign: 'center', border: 'none' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 48, marginBottom: 20 }}>🔐</div>
            <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 12 }}>Sign In Required</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 16, marginBottom: 32, lineHeight: 1.6 }}>
              You need to be logged in to add items to your cart and start shopping.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => navigate('/login')} className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                Sign In
              </button>
              <button onClick={() => navigate('/register/buyer')} className="btn-outline" style={{ width: '100%', justifyContent: 'center' }}>
                Register as Buyer
              </button>
              <button onClick={() => setShowLoginPrompt(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <Link
        to={`/bazaar/product/${product.id}`}
        style={{ textDecoration: 'none', display: 'block' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setImgIndex(0); }}
      >
        <div 
          className="creative-card"
          style={{
            padding: 0,
            display: 'flex',
            flexDirection: isListMode ? 'row' : 'column',
            height: '100%',
            overflow: 'hidden',
            border: hovered ? '1px solid var(--primary-light)' : '1px solid rgba(0,0,0,0.04)',
            transform: hovered && !isListMode ? 'translateY(-4px)' : 'none',
          }}
        >

          {/* Image section */}
          <div style={{ position: 'relative', flexShrink: 0, width: isListMode ? 240 : '100%', height: isListMode ? 'auto' : 240 }}>
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${name}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: outOfStock ? 0.45 : 1,
                  transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  transform: hovered ? 'scale(1.05)' : 'scale(1)',
                }}
              />
            ) : (
              <div style={{ width: '100%', height: '100%', background: 'var(--bg-subtle)', opacity: outOfStock ? 0.45 : 1 }} />
            )}

            {/* Trending badge */}
            {showTrending && order_count > 0 && (
              <div style={{ ...badgeStyle, left: 12, right: 'auto', background: 'rgba(239, 68, 68, 0.9)', color: '#fff' }}>
                🔥 {order_count} sold
              </div>
            )}

            {/* Package badge */}
            {Number(is_package) === 1 && (
              <div style={{ ...badgeStyle, bottom: 12, right: 12, background: 'rgba(139, 92, 246, 0.9)', color: '#fff' }}>
                📦 Package
              </div>
            )}

            {/* Discount badge */}
            {discount_price && Number(discount_price) < Number(price) && (
              <div style={{ ...badgeStyle, bottom: 12, right: 12, background: 'rgba(239, 68, 68, 0.9)', color: '#fff' }}>
                -{Math.round((1 - Number(discount_price) / Number(price)) * 100)}% Sale
              </div>
            )}

            {/* Photo count badge */}
            {photos.length > 1 && (
              <div style={{ ...badgeStyle, bottom: 12, right: discount_price && Number(discount_price) < Number(price) ? 110 : 12 }}>
                📷 {photos.length}
              </div>
            )}

            {/* Status badges */}
            {outOfStock && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(2px)' }}>
                <span style={{ background: '#ef4444', color: '#fff', fontWeight: 900, fontSize: 12, padding: '6px 16px', borderRadius: 100, textTransform: 'uppercase' }}>Sold Out</span>
              </div>
            )}
            {lowStock && (
              <div style={{ position: 'absolute', top: 12, left: 12 }}>
                <span style={{ background: '#f59e0b', color: '#fff', fontWeight: 800, fontSize: 11, padding: '4px 12px', borderRadius: 100 }}>Only {stock_quantity} Left</span>
              </div>
            )}
          </div>

          {/* Body */}
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className="section-tag" style={{ margin: 0, padding: '4px 12px', fontSize: 10 }}>{category}</span>
              {!outOfStock && !lowStock && (
                <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 800 }}>● {t.inStock || 'In Stock'}</span>
              )}
            </div>

            <h3 style={{ fontSize: 18, fontWeight: 800, color: 'var(--secondary)', lineHeight: 1.3, margin: 0 }}>
              {name}
              {Number(is_package) === 1 && <span style={{ fontSize: 11, fontWeight: 700, color: '#8b5cf6', marginLeft: 8, background: 'rgba(139, 92, 246, 0.1)', padding: '2px 8px', borderRadius: 6 }}>BUNDLE</span>}
            </h3>

            {Number(is_package) === 1 && package_items && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 4 }}>
                {(() => {
                  try {
                    const items = typeof package_items === 'string' ? JSON.parse(package_items) : package_items;
                    return items.map((item, i) => (
                      <span key={i} style={{ display: 'inline-block', background: 'rgba(139, 92, 246, 0.06)', padding: '2px 8px', borderRadius: 6, margin: '2px 4px 2px 0', fontSize: 11 }}>
                        {item.quantity}x {item.name}
                      </span>
                    ));
                  } catch { return null; }
                })()}
              </div>
            )}

            {isListMode && description && (
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>{description.slice(0, 150)}{description.length > 150 ? '…' : ''}</p>
            )}

            {/* Price & Seller */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 'auto', paddingTop: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', marginBottom: 2 }}>Price</div>
                <div style={{ fontSize: 20, fontWeight: 900, color: discount_price ? '#ef4444' : 'var(--primary)' }}>
                  {discount_price && Number(discount_price) < Number(price) ? (
                    <>
                      <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', fontSize: 16, fontWeight: 600, marginRight: 8 }}>ETB {Number(price).toLocaleString()}</span>
                      ETB {Number(discount_price).toLocaleString()}
                    </>
                  ) : (
                    <>ETB {Number(price).toLocaleString()}</>
                  )}
                </div>
              </div>
              <Link
                to={`/bazaar/seller/${seller_id}`}
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 700, textDecoration: 'none', textAlign: 'right' }}
              >
                🏪 {seller_name}
                {seller_location && (
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 600, marginTop: 2 }}>📍 {seller_location}</span>
                )}
              </Link>
            </div>

            {/* Add to cart button */}
            <button
              onClick={handleAddToCart}
              disabled={outOfStock}
              className={outOfStock ? "" : "btn-primary"}
              style={{
                marginTop: 8,
                width: '100%',
                justifyContent: 'center',
                padding: '12px 0',
                fontSize: 14,
                ...(outOfStock ? { background: 'var(--bg-subtle)', color: 'var(--text-muted)', cursor: 'not-allowed' } : {}),
                ...(addedFeedback ? { background: '#22c55e', boxShadow: '0 10px 25px rgba(34, 197, 94, 0.2)' } : {}),
              }}
            >
              {outOfStock ? t.outOfStock : addedFeedback ? '✓ Added' : t.addToCart}
            </button>
          </div>
        </div>
      </Link>
    </>
  );
}

const badgeStyle = {
  position: 'absolute',
  bottom: 12,
  right: 12,
  background: 'rgba(255,255,255,0.9)',
  color: 'var(--secondary)',
  fontSize: 11,
  fontWeight: 800,
  padding: '4px 10px',
  borderRadius: 100,
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
};
